import { Plus, X } from 'lucide-react';
import { staleTimes, useMutation, useQuery } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import {
  Button,
  FilterMenu,
  PageHeader,
  SearchBox,
  ToolbarSpacer,
  formatDateTime,
  formatNumber,
  type Column,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useRef, useState } from 'react';
import { LoadMore } from '../LoadMore.tsx';
import { ResourceTable } from '../ResourceTable.tsx';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { useShortcuts } from '../useShortcuts.ts';
import { delegationChoicesOf, governorateChoicesOf, keepingCurrent } from '../geography/area-choices.ts';
import { useGeoAreas } from '../geography/useGeoAreas.ts';
import { initialsOf } from '../../shell/UserMenu.tsx';
import { CustomerRecord } from './CustomerRecord.tsx';
import { CustomerSheet } from './CustomerSheet.tsx';
import {
  canEdit,
  ORDERS_WRITE_ROLE,
  type Customer,
  type CustomerBody,
  type CustomerFilters,
} from './customers.ts';
import styles from './Customers.module.css';
import { useCustomerList } from './useCustomerList.ts';

type Editor = { readonly kind: 'closed' } | { readonly kind: 'form'; readonly customer: Customer | null };

const CLOSED: Editor = { kind: 'closed' };

export type CustomersPageProps = {
  api: TypedClient;
  filters: CustomerFilters;
  openCustomerId: string | null;
  onFiltersChange: (filters: CustomerFilters) => void;
  onOpenCustomer: (customerId: string | null) => void;
};

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const CustomersPage = ({
  api,
  filters,
  openCustomerId,
  onFiltersChange,
  onOpenCustomer,
}: CustomersPageProps) => {
  const { t, locale, tenantId, notify, invalidate } = usePageContext();
  const auth = useAuth();
  const canWrite = auth.hasRole(ORDERS_WRITE_ROLE);
  const searchRef = useRef<HTMLInputElement>(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor>(CLOSED);
  const list = useCustomerList(api, filters, phone);
  const geography = useGeoAreas(api).data;

  const opened = useQuery({
    queryKey: queryKeys.customer(tenantId, openCustomerId ?? ''),
    queryFn: () => api.get('/admin/customers/{id}', { path: { id: openCustomerId ?? '' } }),
    enabled: openCustomerId !== null,
    staleTime: staleTimes.orders,
  });

  const editing = editor.kind === 'form' ? editor.customer : null;

  const save = useMutation({
    mutationFn: async (body: CustomerBody) =>
      editing === null
        ? { ...(await api.post('/admin/customers', { body })), ordersUpdated: 0 }
        : api.patch('/admin/customers/{id}', {
            path: { id: editing.id },
            body: { ...body, version: editing.version },
          }),
    onSuccess: async (saved) => {
      setEditor(CLOSED);
      notify(
        saved.ordersUpdated === 0
          ? t('customers.saved')
          : t('customers.savedWithOrders', { count: saved.ordersUpdated }),
      );
      onOpenCustomer(saved.id);
      await invalidate('customers', 'orders');
    },
  });

  const closeEditor = () => {
    setEditor(CLOSED);
    save.reset();
  };

  const openCreate = () => {
    save.reset();
    setEditor({ kind: 'form', customer: null });
  };

  useShortcuts({
    '/': () => searchRef.current?.focus(),
    ...(canWrite ? { n: openCreate } : {}),
  });

  const columns: readonly Column<Customer>[] = [
    {
      id: 'name',
      header: t('customers.name'),
      primary: true,
      cell: (customer) => (
        <span className={styles['person']}>
          <span className={styles['initials']} aria-hidden="true">
            {initialsOf(customer.name, customer.name)}
          </span>
          <span className={styles['name']}>{customer.name}</span>
        </span>
      ),
    },
    {
      id: 'phone',
      header: t('customers.phone'),
      cell: (customer) => <span className={styles['phone']}>{customer.phone}</span>,
    },
    {
      id: 'governorate',
      header: t('customers.governorate'),
      cell: (customer) => customer.governorate ?? '—',
    },
    { id: 'city', header: t('customers.city'), cell: (customer) => customer.city ?? '—' },
    {
      id: 'createdAt',
      header: t('customers.createdAt'),
      cell: (customer) => formatDateTime(customer.createdAt, locale),
    },
  ];

  const searching = phone !== null || filters.governorate !== null || filters.city !== null;
  const record = opened.data?.id === openCustomerId ? opened.data : null;

  return (
    <>
      <PageHeader
        title={t('nav.customers')}
        context={t('customers.context')}
        action={
          canWrite ? (
            <Button icon={<Plus size={20} aria-hidden="true" />} onClick={openCreate}>
              {t('customers.newCustomer')}
            </Button>
          ) : undefined
        }
      />
      <ResourceTable
        query={list.query}
        columns={columns}
        rows={list.customers}
        rowKey={(customer) => customer.id}
        activeKey={openCustomerId}
        onRowClick={(customer) => {
          onOpenCustomer(customer.id);
        }}
        emptyTitle={searching ? t('customers.noMatch') : t('customers.noCustomers')}
        emptyDescription={searching ? t('customers.noMatchHelp') : t('customers.noCustomersHelp')}
        toolbar={
          <>
            <SearchBox
              ref={searchRef}
              label={t('customers.searchPhone')}
              placeholder={t('customers.searchPhone')}
              type="tel"
              inputMode="tel"
              dir="ltr"
              shortcut="/"
              value={phoneDraft}
              onChange={(event) => {
                setPhoneDraft(event.target.value);
              }}
              onSearch={(text) => {
                setPhone(orNull(text));
              }}
            />
            {phone === null ? null : (
              <Button
                icon={<X size={20} aria-hidden="true" />}
                variant="ghost"
                onClick={() => {
                  setPhoneDraft('');
                  setPhone(null);
                }}
              >
                {t('customers.clearSearch')}
              </Button>
            )}
            <FilterMenu
              label={t('customers.governorate')}
              options={keepingCurrent(governorateChoicesOf(geography, 'name'), filters.governorate ?? '')}
              value={filters.governorate}
              clearLabel={t('orders.filter_remove', { name: t('customers.governorate') })}
              onChange={(governorate) => {
                onFiltersChange({ governorate, city: null });
              }}
            />
            {filters.governorate === null && filters.city === null ? null : (
              <FilterMenu
                label={t('customers.city')}
                options={keepingCurrent(
                  delegationChoicesOf(geography, 'name', filters.governorate),
                  filters.city ?? '',
                )}
                value={filters.city}
                clearLabel={t('orders.filter_remove', { name: t('customers.city') })}
                onChange={(city) => {
                  onFiltersChange({ ...filters, city });
                }}
              />
            )}
            <ToolbarSpacer />
          </>
        }
      />
      {list.customers.length === 0 ? null : (
        <LoadMore
          shownLabel={t('customers.shown', {
            shown: formatNumber(list.customers.length, locale),
            total: formatNumber(list.total, locale),
          })}
          hasMore={list.hasMore}
          loading={list.loadingMore}
          onLoadMore={list.loadMore}
        />
      )}
      {record === null || editor.kind === 'form' ? null : (
        <CustomerRecord
          customer={record}
          editable={canEdit(record, canWrite)}
          onEdit={() => {
            save.reset();
            setEditor({ kind: 'form', customer: record });
          }}
          onClose={() => {
            onOpenCustomer(null);
          }}
        />
      )}
      {editor.kind === 'form' ? (
        <CustomerSheet
          api={api}
          key={editing?.id ?? 'new'}
          editing={editing}
          submitting={save.isPending}
          error={save.error}
          onOpenHolder={(customerId) => {
            closeEditor();
            onOpenCustomer(customerId);
          }}
          onClose={closeEditor}
          onSubmit={(body) => {
            save.mutate(body);
          }}
        />
      ) : null}
    </>
  );
};
